import matter from 'gray-matter'
import { ArrowLeft, Calendar, Clock, User } from 'lucide-react'
import React from 'react'
import { Helmet } from 'react-helmet-async'
import ReactMarkdown from 'react-markdown'
import { useParams, Link } from 'react-router-dom'
import remarkGfm from 'remark-gfm'

import HeaderSpacing from '~/shared/components/HeaderSpace/HeaderSpace'

import styles from './BlogPost.module.scss'
import BlogItem, { type BlogPostFrontmatter } from './components/BlogItem'
import StandaloneQuote from './components/StandaloneQuote'
import { getBlogPostSlug, getRecommendations } from './data/blogPostsData'
import { getBlogImageFallbackSrc, getBlogImageSrc } from './utils/getBlogImageSrc'
import { isDraftPost } from './utils/isDraftPost'
import { parseShortcodes } from './utils/parseShortcodes'

const blogContext = require.context('../../content/blog', false, /\.md$/)

const normalizeDateToIso = (dateStr?: string): string => {
  if (!dateStr) return ''
  const parsed = new Date(dateStr)
  if (Number.isNaN(parsed.getTime())) return ''

  const year = parsed.getFullYear()
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}T00:00:00Z`
}

const getDescription = (description?: string, body?: string): string => {
  if (description?.trim()) {
    return description.trim().replace(/\s+/g, ' ')
  }

  const excerpt = body
    ?.replace(/!\[[^\]]*]\([^)]+\)/g, ' ')
    .replace(/\[([^\]]+)]\([^)]+\)/g, '$1')
    .replace(/[#>*_`~-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 220)

  return excerpt || 'Urus Advisory'
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return isNaN(d.getTime())
    ? dateStr
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const BlogPostImage: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = (props) => {
  const { src, alt, title, onError, ...rest } = props
  const node = (props as { node?: { properties?: { title?: string } } }).node
  const caption = title ?? node?.properties?.title ?? ''
  const imageSrc = getBlogImageSrc(typeof src === 'string' ? src : undefined)
  const fallbackSrc = getBlogImageFallbackSrc(typeof src === 'string' ? src : undefined)
  const [hasError, setHasError] = React.useState(false)
  const [forceFallback, setForceFallback] = React.useState(false)

  React.useEffect(() => {
    setHasError(false)
    setForceFallback(false)
  }, [imageSrc, fallbackSrc])

  return (
    <figure className={styles.bodyFigure}>
      {imageSrc && !hasError ? (
        forceFallback && fallbackSrc ? (
          <img
            src={fallbackSrc}
            alt={alt ?? ''}
            onError={(event) => {
              setHasError(true)
              onError?.(event)
            }}
            {...rest}
          />
        ) : fallbackSrc ? (
          <picture>
            <source srcSet={imageSrc} type="image/webp" />
            <img
              src={fallbackSrc}
              alt={alt ?? ''}
              onError={(event) => {
                setForceFallback(true)
                onError?.(event)
              }}
              {...rest}
            />
          </picture>
        ) : (
          <img
            src={imageSrc}
            alt={alt ?? ''}
            onError={(event) => {
              setHasError(true)
              onError?.(event)
            }}
            {...rest}
          />
        )
      ) : (
        <div className={styles.bodyImagePlaceholder}>Image unavailable</div>
      )}
      {caption ? (
        <figcaption className={styles.bodyFigcaption}>{String(caption)}</figcaption>
      ) : null}
    </figure>
  )
}

const BlogPostHeroImage: React.FC<{
  src?: string
  alt: string
}> = ({ src, alt }) => {
  const fallbackSrc = getBlogImageFallbackSrc(src)
  const [hasError, setHasError] = React.useState(false)
  const [forceFallback, setForceFallback] = React.useState(false)

  React.useEffect(() => {
    setHasError(false)
    setForceFallback(false)
  }, [src, fallbackSrc])

  if (!src || hasError) {
    return <div className={styles.heroPlaceholder} />
  }

  if (forceFallback && fallbackSrc) {
    return (
      <img
        src={fallbackSrc}
        alt={alt}
        className={styles.heroImage}
        onError={() => setHasError(true)}
      />
    )
  }

  if (fallbackSrc) {
    return (
      <picture className={styles.heroPicture}>
        <source srcSet={src} type="image/webp" />
        <img
          src={fallbackSrc}
          alt={alt}
          className={styles.heroImage}
          onError={() => setForceFallback(true)}
        />
      </picture>
    )
  }

  return <img src={src} alt={alt} className={styles.heroImage} onError={() => setHasError(true)} />
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const siteUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://urusadvisory.com'
  const [post, setPost] = React.useState<{
    slug: string
    frontmatter: BlogPostFrontmatter
    body: string
  } | null>(null)

  React.useEffect(() => {
    if (!slug) return
    try {
      const matchedKey = blogContext.keys().find((key) => {
        const fileSlug = key.replace(/^\.\//, '').replace(/\.md$/, '')
        const raw = blogContext(key) as string
        const parsed = matter(raw)
        const frontmatter = parsed.data as BlogPostFrontmatter

        if (isDraftPost(frontmatter.draft)) {
          return false
        }

        const routeSlug = getBlogPostSlug(frontmatter, fileSlug)

        return slug === routeSlug || slug === fileSlug
      })

      if (matchedKey) {
        const fileSlug = matchedKey.replace(/^\.\//, '').replace(/\.md$/, '')
        const raw = blogContext(matchedKey) as string
        const parsed = matter(raw)
        const frontmatter = parsed.data as BlogPostFrontmatter

        if (isDraftPost(frontmatter.draft)) {
          setPost(null)
          return
        }
        setPost({
          slug: getBlogPostSlug(frontmatter, fileSlug),
          frontmatter,
          body: parsed.content,
        })
      } else {
        setPost(null)
      }
    } catch {
      setPost(null)
    }
  }, [slug])

  if (!slug || !post) {
    return (
      <div className={styles.wrapper}>
        <HeaderSpacing />
        <div className={styles.contentSection}>
          <div className={styles.container}>
            <p>Post not found.</p>
            <Link to="/blog" className={styles.backLink}>
              <ArrowLeft size={16} aria-hidden />
              BACK TO INSIGHTS
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const title = String(post.frontmatter.title ?? '')
  const image = post.frontmatter.image
  const imageAlt = post.frontmatter.imageAlt?.trim()
  const imageCaption = post.frontmatter.imageCaption
  const imageAttribution = post.frontmatter.imageAttribution?.trim()
  const category = post.frontmatter.category
  const readTime = post.frontmatter.readTime
  const date = post.frontmatter.date
  const publishedTime = normalizeDateToIso(date)
  const author = post.frontmatter.author
  const tags = post.frontmatter.tags
  const description = getDescription(post.frontmatter.description, post.body)

  const ogImageRaw = post.frontmatter.ogImage
  const imageSrc = getBlogImageSrc(image)
  const ogImageSrc = getBlogImageSrc(ogImageRaw) || getBlogImageFallbackSrc(image) || imageSrc
  const absoluteImageSrc = ogImageSrc
    ? ogImageSrc.startsWith('http')
      ? ogImageSrc
      : `${siteUrl}${ogImageSrc}`
    : `${siteUrl}/images/preview.jpg`
  const canonicalUrl = `${siteUrl}/blog/${post.slug}/`
  const formattedDate = formatDate(date)
  // Recommended Read: up to 3 posts by tag match, then by date (see blogPostsData.getRecommendations)
  const recommendations = getRecommendations(post.slug, tags, 3)

  return (
    <>
      <Helmet>
        <title>{title} | Urus Advisory</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={absoluteImageSrc} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={imageAlt || title} />
        <meta property="og:title" content={title || 'Urus Advisory'} />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content="Urus Advisory" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title || 'Urus Advisory'} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={absoluteImageSrc} />
        <meta name="twitter:image:alt" content={imageAlt || title} />
        <link rel="icon" href="/assets/favicon.ico" />
        <meta property="article:published_time" content={publishedTime} />
        <meta property="article:author" content={author || 'Urus Advisory'} />
        <meta property="author" content={author || 'Urus Advisory'} />
      </Helmet>
      <div className={styles.wrapper}>
        <HeaderSpacing />
        <div className={styles.hero}>
          <div className={styles.heroInner}>
            <BlogPostHeroImage src={imageSrc} alt={imageAlt || title || 'Blog post image'} />
            <div className={styles.heroFade} />
          </div>
        </div>
        {(imageCaption || imageAttribution) && (
          <div className={styles.heroImageCaptionWrap}>
            {imageCaption ? <p className={styles.heroImageCaption}>{imageCaption}</p> : null}
            {imageAttribution ? (
              <p className={styles.heroImageAttribution}>{imageAttribution}</p>
            ) : null}
          </div>
        )}
        <article className={styles.article}>
          <div className={styles.contentSection}>
            <div className={styles.container}>
              <h1 className={styles.title}>{title}</h1>
              <div className={styles.meta}>
                {category && <span className={styles.metaCategory}>{category}</span>}
                {category && (readTime || formattedDate || author) && (
                  <span className={styles.metaSeparator} aria-hidden>
                    |
                  </span>
                )}
                {readTime && (
                  <span className={styles.metaItem}>
                    <Clock size={14} aria-hidden />
                    {readTime}
                  </span>
                )}
                {readTime && (formattedDate || author) && (
                  <span className={styles.metaSeparator} aria-hidden>
                    |
                  </span>
                )}
                {formattedDate && (
                  <span className={styles.metaItem}>
                    <Calendar size={14} aria-hidden />
                    {formattedDate}
                  </span>
                )}
                {formattedDate && author && (
                  <span className={styles.metaSeparator} aria-hidden>
                    |
                  </span>
                )}
                {author && (
                  <span className={styles.metaItem}>
                    <User size={14} aria-hidden />
                    {author}
                  </span>
                )}
              </div>
              <div className={styles.body}>
                {parseShortcodes(post.body).map((segment, i) =>
                  segment.type === 'markdown' ? (
                    <div key={i} className={styles.bodyMarkdown}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{ img: BlogPostImage }}
                      >
                        {segment.content}
                      </ReactMarkdown>
                    </div>
                  ) : segment.type === 'font' ? (
                    <span
                      key={i}
                      className={[
                        styles.bodyFontSpan,
                        segment.size &&
                          styles[
                            `bodySize${segment.size.charAt(0).toUpperCase()}${segment.size.slice(1)}` as keyof typeof styles
                          ],
                        segment.weight &&
                          styles[
                            `bodyWeight${segment.weight.charAt(0).toUpperCase()}${segment.weight.slice(1)}` as keyof typeof styles
                          ],
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{segment.content}</ReactMarkdown>
                    </span>
                  ) : (
                    <StandaloneQuote key={i} quote={segment.quote} author={segment.author} />
                  )
                )}
              </div>
              <div className={styles.backLinkSeparator} aria-hidden />
              <Link to="/blog" className={styles.backLink}>
                <ArrowLeft size={16} aria-hidden />
                BACK TO INSIGHTS
              </Link>
            </div>
          </div>
          {recommendations.length > 0 && (
            <div className={styles.recommendationsWrapper}>
              <section className={styles.recommendations}>
                <h2 className={styles.recommendationsTitle}>Recommended Read</h2>
                <div className={styles.recommendationsGrid}>
                  {recommendations.map((rec) => (
                    <BlogItem key={rec.slug} slug={rec.slug} frontmatter={rec.frontmatter} />
                  ))}
                </div>
              </section>
            </div>
          )}
        </article>
      </div>
    </>
  )
}

export default BlogPost
